import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Science as ExperimentIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TemplateSelectionStep, ExperimentCreationStep } from './components';
import { useExperiments } from './hooks/useExperiments';

const CreateExperiment = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const { createExperiment } = useExperiments();

  const handleTemplateSelected = template => {
    setSelectedTemplate(template);
    setCurrentStep(2);
  };

  const handleBackToTemplateSelection = () => {
    setCurrentStep(1);
    setSelectedTemplate(null);
  };

  const handleExperimentCreated = async experimentData => {
    try {
      setLoading(true);
      setError(null);

      await createExperiment(experimentData);
      setSuccess(true);

      // Redirect to experiments list after a short delay
      setTimeout(() => {
        navigate('/experiments');
      }, 2000);
    } catch (err) {
      // Error creating experiment
      setError(err.message || 'Failed to create experiment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate('/experiments');
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ExperimentIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom color="success.main">
            Experiment Created Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your experiment has been created and is ready to be started.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/experiments')}
            size="large"
          >
            View Experiments
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/experiments')}
            sx={{ mr: 2 }}
          >
            Back to Experiments
          </Button>
        </Box>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ExperimentIcon sx={{ mr: 2, color: 'primary.main' }} />
          Create New Experiment
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Set up an A/B test to compare different versions of your prompt
          templates
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Step Indicator */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: currentStep >= 1 ? 'primary.main' : 'grey.300',
                color: currentStep >= 1 ? 'white' : 'grey.600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                mr: 1,
              }}
            >
              1
            </Box>
            <Typography variant="body1" sx={{ mr: 3 }}>
              Select Template
            </Typography>
          </Box>

          <Box
            sx={{ width: 40, height: 2, backgroundColor: 'grey.300', mx: 2 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: currentStep >= 2 ? 'primary.main' : 'grey.300',
                color: currentStep >= 2 ? 'white' : 'grey.600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                mr: 1,
              }}
            >
              2
            </Box>
            <Typography variant="body1">Configure Experiment</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Content */}
      {currentStep === 1 && (
        <TemplateSelectionStep
          open={true}
          onClose={handleClose}
          onTemplateSelected={handleTemplateSelected}
          selectedTemplateId={null}
        />
      )}

      {currentStep === 2 && selectedTemplate && (
        <ExperimentCreationStep
          open={true}
          onClose={handleClose}
          onSubmit={handleExperimentCreated}
          selectedTemplate={selectedTemplate}
          onBack={handleBackToTemplateSelection}
        />
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </Container>
  );
};

export default CreateExperiment;
